(() => {
  if (window.OnyxQuizApp) {
    window.OnyxQuizApp.mountAll();
    return;
  }
  // DEBUG LOGS (SET TO FALSE FOR PRODUCTION)
  const ENABLE_QUIZ_DEBUG_LOGS = true;
  // 
  const QUIZ_LOG_PREFIX = "Help Me Choose";
  const DAYS_PER_RECOMMENDATION = 7;
  const SLIDE_ANIMATION_TIMEOUT = 450;

  const debug = {
    group(label, callback) {
      if (!ENABLE_QUIZ_DEBUG_LOGS) {
        return;
      }

      console.groupCollapsed(`[${QUIZ_LOG_PREFIX}] ${label}`);

      try {
        callback();
      } finally {
        console.groupEnd();
      }
    },

    log(...values) {
      if (ENABLE_QUIZ_DEBUG_LOGS) {
        console.log(...values);
      }
    },

    table(rows) {
      if (ENABLE_QUIZ_DEBUG_LOGS) {
        console.table(rows);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Data normalization
  // ---------------------------------------------------------------------------

  const asArray = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    if (value === null || value === undefined || value === "") {
      return [];
    }

    return [value];
  };

  const asNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const parsePayload = (payload) => {
    if (typeof payload !== "string") {
      return payload;
    }

    try {
      return JSON.parse(payload);
    } catch (error) {
      console.warn("Help Me Choose: invalid Kirby quiz JSON.", error);
      return null;
    }
  };

  const productHandle = (product) => {
    if (typeof product === "string") {
      return product;
    }

    return product?.shopify_slug || "";
  };

  const normalizeEffects = (effects) => {
    const normalized = [
      ...new Set(
        asArray(effects)
          .flatMap((effect) => String(effect).split(","))
          .map((effect) => effect.trim())
          .filter(Boolean)
      )
    ];

    return normalized.includes("none") ? ["none"] : normalized;
  };

  const normalizeAnswer = (answer) => {
    const products = [...new Set(asArray(answer.products).map(productHandle).filter(Boolean))];
    const grams = asNumber(answer.grams);
    const multiplier = asNumber(answer.multiplier);

    return {
      key: String(answer.key ?? ""),
      label: String(answer.label ?? ""),
      hint: String(answer.hint ?? ""),
      effects: normalizeEffects(answer.effects),
      products,
      pointValue: asNumber(answer.point_value) ?? 0,
      grams,
      multiplier
    };
  };

  const normalizeQuestion = (question) => ({
    key: String(question.key ?? ""),
    question: String(question.question ?? ""),
    answers: asArray(question.answers)
      .map(normalizeAnswer)
      .filter((answer) => answer.key && answer.label)
  });

  const normalizeThreshold = (threshold) => {
    const quantity = asNumber(threshold.variant_quantity);
    const grams = asNumber(threshold.variant_weight_grams);
    const ounces = asNumber(threshold.variant_weight_oz);
    const pounds = asNumber(threshold.variant_weight_lbs);

    return {
      variantTitle: String(threshold.variant_title ?? ""),
      quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
      grams,
      ounces: ounces ?? (grams === null ? null : grams / 28.349523125),
      pounds: pounds ?? (grams === null ? null : grams / 453.59237)
    };
  };

  const normalizeConfig = (payload) => {
    const parsed = parsePayload(payload);
    const source = parsed?.content ?? parsed;

    if (!source || !Array.isArray(source.questions) || source.questions.length === 0) {
      return null;
    }

    const questions = source.questions
      .map(normalizeQuestion)
      .filter((question) => question.key && question.question && question.answers.length > 0);

    if (questions.length === 0) {
      return null;
    }

    const thresholds = asArray(source.variant_thresholds)
      .map(normalizeThreshold)
      .filter((threshold) => threshold.variantTitle && threshold.grams > 0)
      .sort((a, b) => a.grams - b.grams);

    const config = {
      intro: {
        title: String(source.intro_heading ?? ""),
        note: String(source.intro_subheading ?? ""),
        prompt: String(source.intro_prompt ?? ""),
        cta: String(source.intro_cta ?? "")
      },
      results: {
        subtitle: String(source.results_subtitle ?? ""),
        fallbackSubtitle: String(source.fallback_recommendation_subtitle ?? "").trim(),
        fallbackLabel: String(source.fallback_recommendation_label ?? "").trim(),
        fallbackHandle: productHandle(asArray(source.fallback_recommendation_product)[0])
      },
      labels: {
        question: String(source.question_label ?? ""),
        back: String(source.back_label ?? ""),
        restart: String(source.restart_label ?? "")
      },
      questions,
      variantThresholds: thresholds
    };

    const requiredText = [
      config.intro.title,
      config.intro.note,
      config.intro.prompt,
      config.intro.cta,
      config.results.subtitle,
      config.labels.question,
      config.labels.back,
      config.labels.restart
    ];

    if (requiredText.some((value) => !value.trim()) || config.variantThresholds.length === 0) {
      return null;
    }

    return config;
  };

  const readJson = (root, selector) => {
    const element = root.querySelector(selector);

    if (!element?.textContent.trim()) {
      return null;
    }

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn(`Help Me Choose: unable to parse ${selector}.`, error);
      return null;
    }
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);

    if (className) {
      element.className = className;
    }

    if (text !== undefined) {
      element.textContent = text;
    }

    return element;
  };

  const createButton = (label, action, data = {}) => {
    const button = createElement("button", action === "answer" ? "option-button" : action, label);
    button.type = "button";
    button.dataset.action = action;

    Object.entries(data).forEach(([key, value]) => {
      button.dataset[key] = value;
    });

    return button;
  };

  const imageSource = (image) => {
    if (typeof image === "string") {
      return image;
    }

    return image?.src || image?.url || "";
  };

  // ---------------------------------------------------------------------------
  // Quiz calculations
  // ---------------------------------------------------------------------------

  const selectedAnswerRecords = (config, answers) =>
    config.questions
      .map((question) => {
        const answer = question.answers.find(
          (candidate) => candidate.key === answers[question.key]
        );

        return answer ? { question, answer } : null;
      })
      .filter(Boolean);

  /**
   * Builds the complete scored product pool and explicitly identifies the
   * highest-scoring finalists. Ties resolve to the first finalist in the
   * sorted pool.
   */
  const calculateProductResult = (answerRecords, productCatalog) => {
    const scores = new Map();

    answerRecords.forEach(({ answer }) => {
      if (!answer.effects.includes("score")) {
        return;
      }

      answer.products.forEach((handle) => {
        if (!productCatalog[handle]) {
          return;
        }

        scores.set(handle, (scores.get(handle) || 0) + answer.pointValue);
      });
    });

    const pool = [...scores.entries()]
      .map(([handle, score]) => ({
        handle,
        title: productCatalog[handle]?.title || handle,
        score
      }))
      .sort((left, right) => right.score - left.score || left.title.localeCompare(right.title));

    if (pool.length === 0) {
      return {
        product: null,
        winner: null,
        pool: [],
        finalists: [],
        hadTie: false
      };
    }

    const highestScore = pool[0].score;
    const finalists = pool.filter((candidate) => candidate.score === highestScore);
    const winner = finalists[0];

    return {
      product: productCatalog[winner.handle] || null,
      winner,
      pool,
      finalists,
      hadTie: finalists.length > 1
    };
  };

  /**
   * Converts the selected baseline and multipliers into a seven-day usage
   * estimate, then chooses the smallest configured size that can contain it.
   */
  const calculateUsageRecommendation = (answerRecords, thresholds) => {
    const baselineRecord = answerRecords.find(
      ({ answer }) => answer.effects.includes("grams") && answer.grams !== null
    );
    const multiplierRecords = answerRecords.filter(
      ({ answer }) => answer.effects.includes("multiplier")
    );
    const baselineGrams = baselineRecord?.answer.grams ?? 0;
    const totalMultiplier = multiplierRecords.reduce(
      (total, { answer }) => total * (answer.multiplier ?? 1),
      1
    );
    const dailyGrams = baselineGrams * totalMultiplier;
    const estimatedGrams = dailyGrams * DAYS_PER_RECOMMENDATION;
    const matchedThreshold = thresholds.find((item) => estimatedGrams <= item.grams);
    const threshold = matchedThreshold || thresholds[thresholds.length - 1];
    const tierMultiplier =
      matchedThreshold || !threshold
        ? 1
        : Math.max(Math.ceil(estimatedGrams / threshold.grams), 1);
    const quantity = (threshold?.quantity ?? 1) * tierMultiplier;

    return {
      title: threshold?.variantTitle ?? "",
      quantity,
      frequencyLabel: "weekly",
      threshold,
      calculation: {
        baselineRecord,
        baselineGrams,
        multiplierRecords,
        totalMultiplier,
        dailyGrams,
        days: DAYS_PER_RECOMMENDATION,
        estimatedGrams
      }
    };
  };

  const findRecommendedVariant = (product, requestedTitle) => {
    const variants = asArray(product.variants);
    const normalizedTitle = requestedTitle.toLowerCase();

    return (
      variants.find((variant) => String(variant.title).toLowerCase() === normalizedTitle) ||
      variants.find((variant) =>
        String(variant.title).toLowerCase().includes(normalizedTitle)
      ) ||
      variants[0] ||
      null
    );
  };

  const formatNumber = (value) =>
    Number.isInteger(value)
      ? String(value)
      : Number(value.toFixed(2)).toString();

  const formatGrams = (value) => `${formatNumber(value)}g`;

  const formatList = (values) => {
    if (values.length < 2) {
      return values[0] || "";
    }

    if (values.length === 2) {
      return `${values[0]} and ${values[1]}`;
    }

    return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
  };

  const recommendationContainerLabel = (size, quantity) => {
    const isBox = size.replace(/\s+/g, "").toLowerCase() === "10oz";

    if (isBox) {
      return quantity === 1 ? "box" : "boxes";
    }

    return quantity === 1 ? "bag" : "bags";
  };

  // ---------------------------------------------------------------------------
  // Optional diagnostic output
  // ---------------------------------------------------------------------------

  const logAnswerSelection = ({ question, answer }, productCatalog) => {
    const hasNoEffect = answer.effects.includes("none");

    debug.group(`Customer selected "${answer.label}" (${answer.key})`, () => {
      debug.log(`Question: ${question.question}`);

      if (hasNoEffect) {
        debug.log("This answer has No Effect, so scoring and usage calculations were bypassed.");
        return;
      }

      if (answer.effects.includes("score")) {
        const scoredProducts = answer.products.filter((handle) => productCatalog[handle]);
        const unavailableProducts = answer.products.filter((handle) => !productCatalog[handle]);
        const pointLabel = answer.pointValue === 1 ? "point" : "points";

        if (scoredProducts.length > 0) {
          debug.log(
            `Scoring: ${formatList(scoredProducts)} received ${
              answer.pointValue
            } ${pointLabel}.`
          );
        } else {
          debug.log("Scoring: no products in this answer are currently available.");
        }

        if (unavailableProducts.length > 0) {
          debug.log(`Skipped unavailable products: ${formatList(unavailableProducts)}.`);
        }
      }

      if (answer.effects.includes("grams") && answer.grams !== null) {
        debug.log(`Usage: per-serving baseline weight was set to ${formatGrams(answer.grams)}.`);
      }

      if (answer.effects.includes("multiplier")) {
        debug.log(
          `Usage: the daily consumption multiplier was set to ×${answer.multiplier ?? 1}.`
        );
      }

      if (answer.effects.length === 0) {
        debug.log("This answer has no scoring or usage effect.");
      }
    });
  };

  const usageFormula = (calculation) => {
    const factors = calculation.multiplierRecords.map(
      ({ answer }) => `${answer.multiplier ?? 1} (${answer.label})`
    );
    const parts = [
      `${formatGrams(calculation.baselineGrams)} baseline`,
      ...factors,
      `${calculation.days} days`
    ];

    return `${parts.join(" × ")} = ${formatGrams(calculation.estimatedGrams)}`;
  };

  const logResultSummary = ({
    productResult,
    usageRecommendation,
    variant,
    variantThresholds
  }) => {
    debug.group("Final recommendation", () => {
      debug.log("Final scored product pool:");
      debug.table(
        productResult.pool.map((candidate) => ({
          product: candidate.title,
          handle: candidate.handle,
          points: candidate.score,
          finalist: candidate.score === productResult.winner.score ? "Yes" : "No"
        }))
      );

      if (productResult.hadTie) {
        debug.log(
          `Tie: yes. ${formatList(
            productResult.finalists.map((candidate) => candidate.title)
          )} tied at ${productResult.winner.score} points; the first finalist was selected: ${
            productResult.winner.title
          }.`
        );
      } else {
        debug.log(
          `Tie: no. ${productResult.winner.title} won with ${productResult.winner.score} points.`
        );
      }

      debug.log(`Estimated weekly usage: ${usageFormula(usageRecommendation.calculation)}.`);
      debug.log("All metafield size thresholds (evaluated smallest to largest):");
      debug.table(
        variantThresholds.map((threshold) => ({
          Variant: threshold.variantTitle,
          Qty: threshold.quantity,
          g: formatNumber(threshold.grams),
          oz: formatNumber(threshold.ounces),
          lbs: formatNumber(threshold.pounds)
        }))
      );

      if (usageRecommendation.threshold) {
        debug.log(
          `Size threshold: ${usageRecommendation.threshold.quantity} × ${
            usageRecommendation.threshold.variantTitle
          } holds up to ${formatGrams(
            usageRecommendation.threshold.grams
          )}.`
        );
      }

      const size = variant.option1 || usageRecommendation.title || variant.title;
      debug.log(
        `Suggested cart: ${usageRecommendation.quantity} × ${size} ${recommendationContainerLabel(
          size,
          usageRecommendation.quantity
        )} ${usageRecommendation.frequencyLabel} (variant ${variant.id}).`
      );
    });
  };

  // ---------------------------------------------------------------------------
  // Rendering and Interactions
  // ---------------------------------------------------------------------------

  const mountQuiz = (root) => {
    if (root.dataset.quizMounted === "true") {
      return;
    }
    root.dataset.quizMounted = "true";
    const kirbyPayload = readJson(root, "[data-quiz-kirby]");
    const productCatalog = readJson(root, "[data-quiz-products]") || {};
    const config = normalizeConfig(kirbyPayload);
    const quiz = root.querySelector(".quiz");
    const container = root.querySelector("[data-quiz-container]");
    const progress = root.querySelector(".progress");
    const progressBar = progress?.querySelector(".bar");
    const note = root.querySelector(".note");
    const title = root.querySelector(".title");

    if (!quiz || !container || !progress || !progressBar || !note || !title) {
      console.error("Help Me Choose: quiz markup is incomplete.");
      return;
    }

    if (!config) {
      console.error("Help Me Choose: live quiz configuration is incomplete.");
      quiz.classList.add("error");
      progress.hidden = true;
      note.hidden = true;
      title.hidden = true;
      container.replaceChildren(
        createElement("p", "quiz-error-message", "This quiz is currently unavailable.")
      );
      return;
    }

    let currentStep = -1;
    let answers = {};
    let transitioning = false;
    let resultsSlide = null;
    const productImages = new Map();

    const prepareProductImage = (product) => {
      if (productImages.has(product.handle)) {
        return productImages.get(product.handle);
      }

      const image = createElement("img");
      const source = imageSource(product.featured_image);
      const width = asNumber(product.featured_image?.width) || 1000;
      const aspectRatio = asNumber(product.featured_image?.aspect_ratio) || 1;
      image.width = width;
      image.height = asNumber(product.featured_image?.height) || Math.round(width / aspectRatio);
      image.alt = product.title;
      image.loading = "eager";
      image.decoding = "async";
      image.fetchPriority = "low";

      const ready = new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });

        if (source) {
          image.src = source;
        }

        if (!source || image.complete) {
          resolve();
        }
      }).then(() => image.decode?.().catch(() => {}));

      const prepared = { image, ready };
      productImages.set(product.handle, prepared);
      return prepared;
    };

    const fallbackProduct = productCatalog[config.results.fallbackHandle];

    const prepareBoxComponents = (product) => {
      const components = new Map();

      asArray(product.box_components).forEach((value) => {
        const component = typeof value === "string" ? { title: value } : value;

        if (component?.title) {
          components.set(`${component.handle || component.title}:${component.size || ""}`, component);
        }
      });

      return [...components.values()].map((component, index) => ({
        ...component,
        preparedImage: imageSource(component.image)
          ? prepareProductImage({
              handle: `${product.handle}:component:${index}`,
              title: component.title,
              featured_image: component.image
            })
          : null
      }));
    };

    if (fallbackProduct) {
      prepareProductImage(fallbackProduct);

      if (fallbackProduct.is_box_set) {
        prepareBoxComponents(fallbackProduct);
      }
    }

    // The title moves into the result slide during its transition. Put it back
    // before restarting so the original document structure is restored.
    const restoreTitlePosition = () => {
      if (title.parentElement !== quiz) {
        quiz.insertBefore(title, container);
      }
    };

    const getAnswerRecords = () => selectedAnswerRecords(config, answers);

    // Keep scrollable slide content separate from the bottom navigation row.
    const createSlide = () => {
      const slide = createElement("div", "slide");
      const content = createElement("div", "slide-content");
      slide.appendChild(content);
      return { slide, content };
    };

    /**
     * Replaces the active screen while coordinating outgoing and incoming
     * animations. The timeout is a fallback for reduced-motion or missed
     * animationend events.
     */
    const replaceSlide = (slide, direction = "forward") => {
      const oldSlide = container.querySelector(".slide");
      const prepareSlide = () => {
        const resultTitle = slide.querySelector("[data-quiz-result-title]");

        if (resultTitle) {
          if (title.closest(".slide")) {
            const previousTitle = createElement("h2", "bajern title");
            previousTitle.dataset.quizResultTitle = title.textContent;
            title.replaceWith(previousTitle);
          }

          quiz.classList.add("final");
          title.textContent = resultTitle.dataset.quizResultTitle;
          resultTitle.replaceWith(title);
        }
      };
      const finishIncoming = () => {
        let finished = false;
        const finish = (event) => {
          if (event && event.target !== slide) {
            return;
          }

          if (finished) {
            return;
          }

          finished = true;
          slide.removeEventListener("animationend", finish);
          container.classList.remove("is-transitioning");
          transitioning = false;
        };

        slide.addEventListener("animationend", finish);
        window.setTimeout(finish, SLIDE_ANIMATION_TIMEOUT);
      };

      if (!oldSlide) {
        transitioning = true;
        container.classList.add("is-transitioning");
        prepareSlide();
        container.replaceChildren(slide);
        finishIncoming();
        return;
      }

      transitioning = true;
      container.classList.add("is-transitioning");
      oldSlide.style.animationName =
        direction === "back" ? "quizSlideOutReverse" : "quizSlideOut";

      let finished = false;
      const finish = () => {
        if (finished) {
          return;
        }

        finished = true;
        oldSlide.remove();
        prepareSlide();
        slide.style.animationName =
          direction === "back"
            ? "quizSlideInReverse"
            : direction === "result"
              ? "quizResultIn"
              : "quizSlideIn";
        container.appendChild(slide);
        finishIncoming();
      };

      oldSlide.addEventListener("animationend", finish, { once: true });
      window.setTimeout(finish, SLIDE_ANIMATION_TIMEOUT);
    };

    const renderIntro = () => {
      currentStep = -1;
      answers = {};
      transitioning = false;
      resultsSlide = null;

      restoreTitlePosition();
      note.textContent = config.intro.note;
      title.textContent = config.intro.title;
      quiz.classList.remove("final");
      progress.classList.remove("active");
      progressBar.style.width = "0%";

      const { slide, content } = createSlide();
      const prompt = createElement("div", "question kapra");
      prompt.innerHTML = config.intro.prompt;

      if (prompt.children.length === 1 && prompt.firstElementChild.tagName === "P") {
        prompt.innerHTML = prompt.firstElementChild.innerHTML;
      }

      const options = createElement("div", "options");
      options.appendChild(createButton(config.intro.cta, "start"));

      content.append(prompt, options);
      container.replaceChildren(slide);
    };

    const renderQuestion = (direction = "forward") => {
      const question = config.questions[currentStep];

      if (!question) {
        return;
      }

      progress.classList.add("active");
      progressBar.style.width = `${(currentStep / config.questions.length) * 100}%`;

      const { slide, content } = createSlide();
      content.appendChild(
        createElement(
          "p",
          "sub",
          `${config.labels.question} ${currentStep + 1}`
        )
      );
      content.appendChild(createElement("h3", "question kapra", question.question));

      const options = createElement("div", "options");

      question.answers.forEach((answer) => {
        const button = createButton(answer.label, "answer", {
          questionKey: question.key,
          answerKey: answer.key
        });

        if (answer.hint) {
          button.appendChild(createElement("span", "hint", answer.hint));
        }

        options.appendChild(button);
      });

      content.appendChild(options);

      if (currentStep > 0) {
        slide.appendChild(createButton(config.labels.back, "back"));
      }

      replaceSlide(slide, direction);
    };

    const renderError = (message) => {
      const { slide, content } = createSlide();
      content.appendChild(createElement("h3", "question kapra", message));
      slide.appendChild(createButton(config.labels.restart, "restart"));
      quiz.classList.remove("final");
      replaceSlide(slide);
    };

    /**
     * Reuses the theme's existing PDP controls so cart behavior, subscription
     * properties, prices, and availability stay consistent with product pages.
     */
    const mountGlobalAtc = (product, initialVariant, initialQuantity) => {
      const template = [...root.querySelectorAll("[data-quiz-atc-template]")]
        .find((item) => item.dataset.quizAtcTemplate === product.handle);
      const renderedAtc = template?.content.cloneNode(true).querySelector(".pdp-globals");

      if (!renderedAtc) {
        return null;
      }

      renderedAtc.querySelectorAll("script").forEach((script) => script.remove());

      const host = createElement("div", "quiz-result-atc");
      host.appendChild(renderedAtc);

      const variants = asArray(product.variants);
      const desktopVariantSelect = host.querySelector("#variant-select");
      const mobileVariantSelect = host.querySelector("#variant-select-mobile");

      if (variants.length === 1) {
        [desktopVariantSelect, mobileVariantSelect].forEach((select) => {
          if (select) {
            select.hidden = true;
          }
        });
      }

      const desktopSubscriptionSelect = host.querySelector("#subscribe-selector");
      const mobileSubscriptionSelect = host.querySelector("#subscribe-selector-mobile");
      const quantitySelector = host.querySelector(".qty-selector");
      const quantityInput = quantitySelector?.querySelector(".qty-val");
      const cartButtons = [...host.querySelectorAll(".atc-button")];
      const subscriptionButtons = [...host.querySelectorAll(".subscribe-atc")];
      const allButtons = [...cartButtons, ...subscriptionButtons];
      const subscriptionOnly =
        product.requires_selling_plan === true ||
        Boolean(host.querySelector(".global-atc-subscription-only"));

      let selectedVariant = initialVariant;

      allButtons.forEach((button) => {
        button.dataset.defaultHtml = button.innerHTML;
      });

      const restoreButtonMarkup = () => {
        allButtons.forEach((button) => {
          if (!button.querySelector(".atc-price, .sub-atc-price")) {
            button.innerHTML = button.dataset.defaultHtml;
          }
        });
      };

      const setButtonDisabled = (button, disabled) => {
        button.classList.toggle("disabled", disabled);
        button.disabled = disabled;
        button.setAttribute("aria-disabled", String(disabled));
      };

      const quantityText = () => quantityInput?.value ?? "1";

      const priceForQuantity = (displayPrice) => {
        const priceText = String(displayPrice ?? "");
        const quantity = Number.parseInt(String(quantityText()).replace(/\D/g, ""), 10) || 1;
        const priceMatch = priceText.match(/\d[\d,.]*/);

        if (quantity <= 1 || !priceMatch) {
          return priceText;
        }

        const priceValue = priceMatch[0];
        const decimalSeparator =
          priceValue.lastIndexOf(",") > priceValue.lastIndexOf(".") ? "," : ".";
        const normalizedPrice = priceValue
          .replace(new RegExp(`[^0-9${decimalSeparator}]`, "g"), "")
          .replace(decimalSeparator, ".");
        const amount = Number.parseFloat(normalizedPrice);

        if (!Number.isFinite(amount)) {
          return priceText;
        }

        const decimalPlaces = priceValue.includes(decimalSeparator)
          ? priceValue.split(decimalSeparator).pop().length
          : 0;
        const multipliedPrice = (amount * quantity)
          .toFixed(decimalPlaces)
          .replace(".", decimalSeparator);

        return priceText.replace(priceValue, multipliedPrice);
      };

      const setQuantity = (value, allowBlank = false) => {
        if (!quantityInput || !quantitySelector) {
          return 1;
        }

        const minimum = Math.max(Number(quantitySelector.dataset.min) || 1, 1);
        const maximum = Math.max(Number(quantitySelector.dataset.max) || 999, minimum);
        const cleanValue = String(value ?? "").replace(/\D/g, "");

        if (allowBlank && cleanValue === "") {
          quantityInput.value = "";
          return "";
        }

        const parsed = Number.parseInt(cleanValue, 10);
        const quantity = Math.min(
          Math.max(Number.isFinite(parsed) ? parsed : minimum, minimum),
          maximum
        );
        quantityInput.value = quantity;
        return quantity;
      };

      const selectedAllocation = (select) => {
        const option = select?.selectedOptions[0];

        if (!option?.dataset.id || option.disabled) {
          return null;
        }

        return asArray(selectedVariant.selling_plan_allocations).find(
          (allocation) => String(allocation.selling_plan_id) === option.dataset.id
        ) || null;
      };

      const subscriptionOptions = (select) => {
        if (!select || select.value === "one-time") {
          return subscriptionOnly ? null : {};
        }

        const allocation = selectedAllocation(select);

        if (!allocation) {
          return null;
        }

        return {
          shipping_interval_frequency: select.value,
          selling_plan: Number(allocation.selling_plan_id)
        };
      };

      const updateMobileCartButtonLabel = () => {
        const button = host.querySelector(".atc-button.mobile-only");
        const price = button?.querySelector(".atc-price");

        if (!button || !price) {
          return;
        }

        const isSubscription = Boolean(selectedAllocation(mobileSubscriptionSelect));
        button.replaceChildren(
          price,
          document.createTextNode(` - ${isSubscription ? "Subscribe" : "Add to Cart"}`)
        );
      };

      const updatePricesAndAvailability = () => {
        if (!selectedVariant) {
          return;
        }

        restoreButtonMarkup();
        const subscriptionUnavailable =
          selectedVariant.available === false ||
          String(selectedVariant.option1).toLowerCase() === "4oz";

        [desktopSubscriptionSelect, mobileSubscriptionSelect].forEach((select) => {
          if (!select) {
            return;
          }

          const planIds = new Set(
            asArray(selectedVariant.selling_plan_allocations)
              .map((allocation) => String(allocation.selling_plan_id))
          );
          const planOptions = [...select.options].filter((option) => option.dataset.id);
          const oneTimeOption = select.querySelector('option[value="one-time"]');

          if (oneTimeOption) {
            oneTimeOption.disabled = subscriptionOnly;
          }

          planOptions.forEach((option) => {
            option.disabled = subscriptionUnavailable || !planIds.has(option.dataset.id);
          });

          const firstPlan = planOptions.find((option) => !option.disabled);
          select.hidden = !firstPlan;

          if (!select.selectedOptions[0] || select.selectedOptions[0].disabled) {
            const oneTime = subscriptionOnly
              ? null
              : select.querySelector('option[value="one-time"]');
            select.value = oneTime?.value ?? firstPlan?.value ?? "";
          }
        });

        host.querySelectorAll(".atc-price").forEach((price) => {
          price.textContent = priceForQuantity(selectedVariant.display_price);
        });

        host.querySelectorAll(".sub-atc-price").forEach((price) => {
          const select = price.closest(".mobile-only")
            ? mobileSubscriptionSelect
            : desktopSubscriptionSelect;
          price.textContent = priceForQuantity(
            selectedAllocation(select)?.display_price ?? selectedVariant.display_price
          );
        });

        const mobileAllocation = selectedAllocation(mobileSubscriptionSelect);

        if (mobileAllocation) {
          const mobilePrice = host.querySelector(".atc-button.mobile-only .atc-price");

          if (mobilePrice) {
            mobilePrice.textContent = priceForQuantity(mobileAllocation.display_price);
          }
        }

        updateMobileCartButtonLabel();

        const unavailable = selectedVariant.available === false;

        cartButtons.forEach((button) => {
          setButtonDisabled(button, unavailable || (subscriptionOnly && !mobileAllocation));
        });
        subscriptionButtons.forEach((button) => {
          const select = button.classList.contains("mobile-only")
            ? mobileSubscriptionSelect
            : desktopSubscriptionSelect;
          const allocation = selectedAllocation(select);
          button.hidden = !allocation;
          setButtonDisabled(button, !allocation);
        });
        const subscriptionControls = host.querySelector(".atc-left");

        if (subscriptionControls) {
          subscriptionControls.hidden =
            !selectedAllocation(desktopSubscriptionSelect) && !mobileAllocation;
        }
      };

      const selectVariant = (variantId) => {
        const nextVariant =
          variants.find((variant) => String(variant.id) === String(variantId)) ||
          selectedVariant ||
          variants[0];

        if (!nextVariant) {
          return;
        }

        selectedVariant = nextVariant;

        if (desktopVariantSelect) {
          desktopVariantSelect.value = String(nextVariant.id);
        }

        if (mobileVariantSelect) {
          mobileVariantSelect.value = String(nextVariant.id);
        }

        updatePricesAndAvailability();
      };

      const addSelectedItem = (button, properties = {}) => {
        if (
          button.disabled ||
          !selectedVariant ||
          (subscriptionOnly && !properties.selling_plan)
        ) {
          return;
        }

        if (!window.CartJS) {
          console.error("Help Me Choose: CartJS is unavailable.");
          return;
        }

        button.textContent = "Going...";

        const variantId = selectedVariant.id;
        const quantity = setQuantity(quantityText());

        window.CartJS.addItem(
          variantId,
          quantity,
          properties,
          {
            success(lineItem) {
              window.show_cart_confirm?.(lineItem);
              const cartCount = document.querySelector("header .cart-count");
              cartCount?.removeAttribute("hidden");
              cartCount?.style.removeProperty("display");
              button.textContent = "Added!";

              window.Shopify?.analytics?.publish?.("custom_page:add_to_cart", {
                variant_id: variantId,
                quantity: quantity,
                source: "custom_landing_page",
                page_path: window.location.pathname,
                page_location: window.location.href
              });
            },
            error(error) {
              console.error("Help Me Choose: unable to add the recommended coffee.", error);
              button.textContent = "Error :(";
            }
          }
        );
      };

      desktopVariantSelect?.addEventListener("change", () => {
        selectVariant(desktopVariantSelect.value);
      });

      mobileVariantSelect?.addEventListener("change", () => {
        selectVariant(mobileVariantSelect.value);
      });

      desktopSubscriptionSelect?.addEventListener("change", updatePricesAndAvailability);
      mobileSubscriptionSelect?.addEventListener("change", updatePricesAndAvailability);

      quantityInput?.addEventListener("input", () => {
        setQuantity(quantityText(), true);
        updatePricesAndAvailability();
      });

      quantityInput?.addEventListener("change", () => {
        setQuantity(quantityText());
        updatePricesAndAvailability();
      });

      quantityInput?.addEventListener("blur", () => {
        setQuantity(quantityText());
        updatePricesAndAvailability();
      });

      host.addEventListener("click", (event) => {
        if (event.target.closest(".qty-up")) {
          setQuantity(setQuantity(quantityText()) + 1);
          updatePricesAndAvailability();
          return;
        }

        if (event.target.closest(".qty-down")) {
          setQuantity(setQuantity(quantityText()) - 1);
          updatePricesAndAvailability();
          return;
        }

        const cartButton = event.target.closest(".atc-button");

        if (cartButton) {
          const properties = cartButton.classList.contains("mobile-only")
            ? subscriptionOptions(mobileSubscriptionSelect)
            : {};

          if (properties !== null) {
            addSelectedItem(cartButton, properties);
          }

          return;
        }

        const subscriptionButton = event.target.closest(".subscribe-atc");

        if (subscriptionButton) {
          const select = subscriptionButton.classList.contains("mobile-only")
            ? mobileSubscriptionSelect
            : desktopSubscriptionSelect;
          const properties = subscriptionOptions(select);

          if (properties !== null) {
            addSelectedItem(subscriptionButton, properties);
          }
        }
      });

      setQuantity(initialQuantity);
      selectVariant(initialVariant.id);

      return host;
    };

    const renderResults = async (showFallback = false) => {
      const answerRecords = getAnswerRecords();
      const productResult = calculateProductResult(answerRecords, productCatalog);
      const product = showFallback
        ? productCatalog[config.results.fallbackHandle]
        : productResult.product;

      if (!product) {
        renderError("We could not find an available coffee for those answers.");
        return;
      }

      const usageRecommendation = calculateUsageRecommendation(
        answerRecords,
        config.variantThresholds
      );
      const variant = findRecommendedVariant(product, usageRecommendation.title);

      if (!variant) {
        renderError("This coffee does not currently have an available size.");
        return;
      }

      const { slide, content } = createSlide();
      const resultTitle = createElement("h2", "bajern title");
      resultTitle.dataset.quizResultTitle = product.title;
      content.append(
        createElement(
          "p",
          "sub",
          showFallback
            ? config.results.fallbackSubtitle || config.results.subtitle
            : config.results.subtitle
        ),
        resultTitle,
        createElement("p", "result-notes kapra upper", product.notes)
      );

      const productElement = createElement("div", "product");
      const productUrl = `${product.url}${
        product.url.includes("?") ? "&" : "?"
      }variant=${variant.id}`;

      const imageContainer = createElement("div", "product-image");
      const imageLink = createElement("a");
      imageLink.href = productUrl;

      const { image, ready } = prepareProductImage(product);
      const imagePromises = [ready];
      image.fetchPriority = "high";
      imageLink.appendChild(image);
      imageContainer.appendChild(imageLink);

      const productMeta = createElement("div", "product-meta");

      if (showFallback && product.is_box_set) {
        const components = prepareBoxComponents(product);

        if (components.length > 0) {
          slide.classList.add("has-box-components");
          productMeta.classList.add("has-box-components");
          const contents = createElement("section", "box-contents");
          const heading = createElement("h3", "box-contents-title kapra", "What's Inside");
          const cards = createElement("ul", "box-components");

          components.forEach((component) => {
            const card = createElement("li", "box-component");
            const visual = createElement("div", "component-visual");
            const ornament = createElement("div", "component-ornament");
            ornament.setAttribute("aria-hidden", "true");
            visual.style.setProperty("--component-color", component.bg_color || "#eceadd");
            visual.appendChild(ornament);

            if (component.preparedImage) {
              const { image: componentImage, ready: componentReady } = component.preparedImage;
              componentImage.className = "component-image";
              componentImage.fetchPriority = "high";
              visual.appendChild(componentImage);
              imagePromises.push(componentReady);
            }

            const details = createElement("p", "component-size");

            if (component.size) {
              details.appendChild(document.createTextNode(`${component.size} | `));
            }

            details.appendChild(createElement("strong", "component-descriptor", "Whole Bean"));
            card.append(visual, details, createElement("h4", "component-title", component.title));
            cards.appendChild(card);
          });

          contents.append(heading, cards);
          productMeta.appendChild(contents);
        }
      } else {
        const recommendationText = createElement("p", "quantity-recommendation");
        const recommendationLabel = createElement("span", "recommendation-label", "You'll need: ");
        const recommendationSize =
          variant.option1 || usageRecommendation.title || variant.title;
        const containerLabel = recommendationContainerLabel(
          recommendationSize,
          usageRecommendation.quantity
        );
        const recommendationValue = createElement(
          "span",
          "recommendation-value",
          `x${usageRecommendation.quantity} ${recommendationSize} ${containerLabel} ${
            usageRecommendation.frequencyLabel
          }`
        );
        recommendationText.append(recommendationLabel, recommendationValue);
        productMeta.appendChild(recommendationText);
      }

      const globalAtc = mountGlobalAtc(
        product,
        variant,
        usageRecommendation.quantity
      );

      if (!globalAtc) {
        renderError("This coffee's add to cart controls are unavailable.");
        return;
      }

      if (showFallback) {
        debug.log(`[${QUIZ_LOG_PREFIX}] Fallback recommendation: ${product.title}.`);
      } else {
        logResultSummary({
          productResult,
          usageRecommendation,
          variant,
          variantThresholds: config.variantThresholds
        });
      }

      const actions = createElement("div", "result-actions");

      if (showFallback) {
        const backButton = createButton(config.labels.back, "results");
        backButton.className = "back";
        actions.appendChild(backButton);
      } else {
        actions.appendChild(createButton(config.labels.restart, "restart"));
        resultsSlide = slide;
      }

      if (
        !showFallback &&
        config.results.fallbackLabel &&
        productCatalog[config.results.fallbackHandle] &&
        config.results.fallbackHandle !== product.handle
      ) {
        actions.appendChild(createButton(config.results.fallbackLabel, "fallback"));
      }

      productElement.appendChild(imageContainer);

      if (productMeta.hasChildNodes()) {
        productElement.appendChild(productMeta);
      }

      content.appendChild(productElement);
      const footer = createElement("div", "slide-footer");
      footer.append(globalAtc, actions);
      slide.appendChild(footer);
      transitioning = true;
      container.setAttribute("aria-busy", "true");
      await Promise.all(imagePromises);
      container.removeAttribute("aria-busy");
      replaceSlide(slide, showFallback ? "forward" : "result");
    };

    const startQuiz = () => {
      currentStep = 0;
      answers = {};
      renderQuestion();
    };

    const selectAnswer = (questionKey, answerKey) => {
      const question = config.questions[currentStep];
      const answer = question?.answers.find((candidate) => candidate.key === answerKey);

      if (!question || question.key !== questionKey || !answer) {
        return;
      }

      answers[questionKey] = answerKey;
      logAnswerSelection({ question, answer }, productCatalog);
      currentStep += 1;

      if (currentStep < config.questions.length) {
        renderQuestion();
      } else {
        renderResults();
      }
    };

    const goBack = () => {
      if (currentStep <= 0) {
        return;
      }

      currentStep -= 1;
      delete answers[config.questions[currentStep].key];
      renderQuestion("back");
    };

    root.addEventListener("click", (event) => {
      const button = event.target.closest("[data-action]");

      if (!button || transitioning) {
        return;
      }

      switch (button.dataset.action) {
        case "start":
          startQuiz();
          break;
        case "answer":
          selectAnswer(button.dataset.questionKey, button.dataset.answerKey);
          break;
        case "back":
          goBack();
          break;
        case "restart":
          renderIntro();
          break;
        case "fallback":
          renderResults(true);
          break;
        case "results":
          if (resultsSlide) {
            replaceSlide(resultsSlide, "back");
          }
          break;
      }
    });

    renderIntro();
  };

  const mountAll = () => {
    document.querySelectorAll("[data-quiz-app]").forEach(mountQuiz);
  };

  window.OnyxQuizApp = {
    mountAll
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountAll, { once: true });
  } else {
    mountAll();
  }
})();
